import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDevice, useSensorData, useMTCommand, useFieldPending } from '@/hooks';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { ToggleSwitch, PendingBadge } from '@/components/common/ConfigControls';
import { findPendingMT } from '@/lib/sensor-commands';
import { formatDateTime } from '@/lib/formatters';
import { resolveOnlineState, onlineStateVariant } from '@/lib/enum-resolvers';
import { isIntegratedXPulseSensor } from '@/lib/product-capabilities';

export function SwitchesPanel() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const { sensorData } = useSensorData();
  const [refreshing, setRefreshing] = useState(false);
  const { sendCommand, mtObjList, feedback, setFeedback } = useMTCommand(deviceUUID ?? '');
  const fieldPending = useFieldPending();
  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.GET_SENSOR_DATA_REQUEST);

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.GET_SENSOR_DATA_REQUEST);
    } finally {
      setRefreshing(false);
    }
  };

  // Mirrors mobile's SensorPageViewModel.SensorSelectedCommandHandler: a
  // single shared MT_MESSAGE_SET_SWITCH_ON/_OFF pair, dataStrList is just
  // the switch instance (a single decimal-string byte — backend's
  // formatMTCommandPayloadSwitchCmd rejects anything else). Use the real
  // switchInstance field rather than mobile's positional-index shortcut
  // (SensorNum), since it's the value the backend actually persists by.
  const handleToggleSwitch = (sw: { switchInstance: number; isSwitchOn: boolean }) => {
    if (!deviceUUID) return;
    setFeedback(null);
    return fieldPending.run(`switch-${sw.switchInstance}`, !sw.isSwitchOn, () =>
      sendCommand(
        sw.isSwitchOn ? NAMTMessageType.MT_MESSAGE_SET_SWITCH_OFF : NAMTMessageType.MT_MESSAGE_SET_SWITCH_ON,
        [String(sw.switchInstance)],
      ),
    );
  };

  const switches = sensorData?.switchSensorList ?? [];

  if (deviceInfoLoading && !selectedDeviceInfo) {
    return <PageSpinner />;
  }

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description="Select a device to view switch sensor data."
      />
    );
  }

  if (switches.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Switches</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Monitor switch sensor states
          </p>
        </div>
        <EmptyState
          title="No Switch Sensors"
          description="No switch sensors are configured on this device."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Switches</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Monitor switch sensor states
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          On-Demand
        </Button>
      </div>

      {feedback && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{feedback.message}</span>
            <button type="button" onClick={() => setFeedback(null)} className="ml-3 text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {switches.map((sw, idx) => {
          const pendingOnMT = findPendingMT(
            mtObjList,
            NAMTMessageType.MT_MESSAGE_SET_SWITCH_ON,
            (d) => d[0] === String(sw.switchInstance),
          );
          const pendingOffMT = findPendingMT(
            mtObjList,
            NAMTMessageType.MT_MESSAGE_SET_SWITCH_OFF,
            (d) => d[0] === String(sw.switchInstance),
          );
          const isOn =
            fieldPending.getOptimisticValue<boolean>(`switch-${sw.switchInstance}`) ??
            (pendingOnMT ? true : pendingOffMT ? false : sw.isSwitchOn);
          const isOnPending =
            fieldPending.isPending(`switch-${sw.switchInstance}`) || !!pendingOnMT || !!pendingOffMT;

          return (
            <div
              key={`switch-${sw.switchInstance}-${idx}`}
              className="rounded-xl border border-slate-200 bg-white p-5 dark:border-navy-700 dark:bg-navy-900"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`h-4 w-4 rounded-full ${
                      isOn
                        ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]'
                        : 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]'
                    }`}
                  />
                  <div>
                    <h3 className="font-medium text-slate-900 dark:text-white">
                      {sw.sensorName || `Switch ${sw.switchInstance}`}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {/* Mobile's "{sensorName}, Num {N}" (updateSwitchCategory,
                          DeviceCategoryMetrics.cs) — switchInstance is used raw, with
                          NO +1 offset (matches AC's pattern, not DC/security's). */}
                      {sw.sensorName}, Num {sw.switchInstance} &middot; {isIntegratedXPulseSensor(sw) ? 'Integrated' : 'External'}
                    </p>
                    <p className={`flex items-center text-sm font-semibold ${isOn ? 'text-red-500' : 'text-green-500'}`}>
                      {isOn ? 'ON (Active)' : 'OFF (Normal)'}
                      {isOnPending && <PendingBadge />}
                    </p>
                  </div>
                </div>
                {isIntegratedXPulseSensor(sw) ? (
                  <StatusBadge label="Built-in" variant="info" dot />
                ) : (
                  <StatusBadge
                    label={resolveOnlineState(sw.onlineState)}
                    variant={onlineStateVariant(sw.onlineState)}
                    dot
                  />
                )}
              </div>

              {/* Toggle */}
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-navy-700">
                <span className="text-xs text-slate-500 dark:text-slate-400">Switch State</span>
                <ToggleSwitch enabled={isOn} loading={false} onToggle={() => handleToggleSwitch(sw)} />
              </div>

              {sw.updatedDT && (
                <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                  Updated: {formatDateTime(sw.updatedDT)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
